import React, { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { getStripePlan, stripeCheckoutLink } from '../../redux/actions/myAccountAction'
import { AppUseSelector } from '../../redux/hooks'
import { membershipNameType } from '../../utils/constant'
import { getPriceInNumber } from '../../utils/helperFunction'
import { formatMoney } from '../../utils/currency'
import { formatDate, formatPercent } from '../../i18n/format'
import { useChineseLang } from '../../i18n/chinese'
import { DRAGON_BOAT_PROMO, getDragonBoatPromoPricing, getDragonBoatPromoTimeLeft } from '../../utils/dragonBoatPromo'
import './dragon-boat-banner.css'

// A translation key: the error is translated at render, so it follows a language switch.
const CHECKOUT_ERROR_MESSAGE = 'offers:promo.checkoutUnavailable'

// Chinese content, the same in every interface language.
const FESTIVAL_HANZI = '端午节'

const pad = (value: number) => String(value).padStart(2, '0')

const DragonBoatBanner: React.FC = () => {
  const { t } = useTranslation('offers')
  const zhLang = useChineseLang()
  const videoRef = useRef<HTMLVideoElement>(null)
  const { credentials } = AppUseSelector(({ user }) => user)
  const [timeLeft, setTimeLeft] = useState(getDragonBoatPromoTimeLeft())
  const [loading, setLoading] = useState(false)
  const [proMonthlyPriceId, setProMonthlyPriceId] = useState('')
  const [regularMonthly, setRegularMonthly] = useState<number | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(getDragonBoatPromoTimeLeft()), 1000)
    return () => clearInterval(timer)
  }, [])

  // Every displayed number comes from this Stripe response — never hardcoded.
  useEffect(() => {
    let isMounted = true

    const loadProMonthly = async () => {
      const plans = await getStripePlan(credentials?.memberId)
      if (!isMounted) return

      const monthlyPlans = Array.isArray(plans?.monthly) ? plans.monthly : []
      const proPlan = monthlyPlans.find((plan: any) => plan.value === membershipNameType.Pro)
      const priceId = proPlan?.priceId
      const price = getPriceInNumber(proPlan?.price)

      if (priceId && price) {
        setProMonthlyPriceId(priceId)
        setRegularMonthly(price)
        setError('')
        return
      }
      setError(CHECKOUT_ERROR_MESSAGE)
    }

    loadProMonthly().catch(() => {
      if (isMounted) setError(CHECKOUT_ERROR_MESSAGE)
    })

    return () => {
      isMounted = false
    }
  }, [credentials?.memberId])

  const handleVideoReady = () => {
    videoRef.current?.play().catch(() => undefined)
  }

  const handleClaim = async (event: React.MouseEvent) => {
    event.stopPropagation()
    if (loading || !proMonthlyPriceId) return

    setLoading(true)
    setError('')

    try {
      const affiliateId =
        typeof window !== 'undefined' && (window as any).affiliateId ? String((window as any).affiliateId) : undefined

      const response: any = await stripeCheckoutLink({
        priceId: proMonthlyPriceId,
        couponCode: DRAGON_BOAT_PROMO.couponCode,
        ...(affiliateId && { affiliateId }),
      })

      if (response?.return_url) {
        window.open(response.return_url, '_self')
        return
      }
      if (response?.url) {
        window.open(response.url, '_self')
        return
      }
      setError(CHECKOUT_ERROR_MESSAGE)
    } catch {
      setError(CHECKOUT_ERROR_MESSAGE)
    } finally {
      setLoading(false)
    }
  }

  const pricing = getDragonBoatPromoPricing(regularMonthly)
  const isExpired = !Object.values(timeLeft).some(Boolean)
  const percent = formatPercent(DRAGON_BOAT_PROMO.discountPercent / 100)
  const endDate = formatDate(DRAGON_BOAT_PROMO.endDate, 'monthDay', { timeZone: 'Asia/Hong_Kong' })
  const countdownUnits = [
    { id: 'days', value: timeLeft.days, label: t('promo.countdown.days', { count: timeLeft.days }) },
    { id: 'hours', value: timeLeft.hours, label: t('promo.countdown.hours', { count: timeLeft.hours }) },
    { id: 'minutes', value: timeLeft.minutes, label: t('promo.countdown.minutes', { count: timeLeft.minutes }) },
    { id: 'seconds', value: timeLeft.seconds, label: t('promo.countdown.seconds', { count: timeLeft.seconds }) },
  ]

  if (isExpired) return null

  return (
    <div className='dbf-banner' onClick={handleClaim} role='button' tabIndex={0}>
      <video
        ref={videoRef}
        className='dbf-banner-video'
        autoPlay
        loop
        muted
        playsInline
        preload='auto'
        onCanPlay={handleVideoReady}
        onLoadedData={handleVideoReady}
      >
        <source src='/videos/dragon-boat-festival.mp4' type='video/mp4' />
      </video>
      <div className='dbf-banner-overlay' />

      <div className='dbf-banner-content'>
        <div className='dbf-banner-left'>
          <div className='dbf-banner-eyebrow'>
            <span className='dbf-banner-eyebrow-hanzi' lang={zhLang}>
              {FESTIVAL_HANZI}
            </span>
            {t('dragonBoat.eyebrow')}
          </div>
          <h5 className='dbf-banner-title'>
            <Trans
              t={t}
              i18nKey='dragonBoat.title'
              values={{ percent }}
              components={{ accent: <span className='dbf-banner-title-accent' /> }}
            />
          </h5>

          {pricing ? (
            <div className='dbf-banner-pricing'>
              <div className='dbf-banner-pricing-line'>
                <span className='dbf-banner-price-strike'>{formatMoney(pricing.regularMonthly, 'USD')}</span>
                <span className='dbf-banner-price-new'>{formatMoney(pricing.promoMonthly, 'USD')}</span>
                <span className='dbf-banner-price-period'>{t('promo.perMonth')}</span>
              </div>
              <div className='dbf-banner-billing-note'>
                {t('dragonBoat.billingNote', {
                  months: DRAGON_BOAT_PROMO.durationMonths,
                  price: formatMoney(pricing.regularMonthly, 'USD'),
                })}
              </div>
            </div>
          ) : (
            <div className='dbf-banner-pricing'>
              <div className='dbf-banner-price-new dbf-banner-price-new--lg'>
                {t('dragonBoat.fallbackPrice', { percent })}
              </div>
              <div className='dbf-banner-billing-note'>
                {t('dragonBoat.fallbackNote', { months: DRAGON_BOAT_PROMO.durationMonths })}
              </div>
            </div>
          )}

          <p className='dbf-banner-subtitle'>{t('dragonBoat.subtitle', { date: endDate })}</p>
          <p className='dbf-banner-subtitle'>{t('common:teacherLanguageNotice')}</p>
        </div>

        <div className='dbf-banner-right'>
          <div className='dbf-banner-countdown'>
            {countdownUnits.map((unit, i) => (
              <React.Fragment key={unit.id}>
                {i > 0 && <span className='dbf-banner-countdown-sep'>:</span>}
                <div className='dbf-banner-countdown-unit'>
                  <span className='dbf-banner-countdown-num'>{pad(unit.value)}</span>
                  <span className='dbf-banner-countdown-label'>{unit.label}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
          <button className='dbf-banner-cta' onClick={handleClaim} disabled={loading || !proMonthlyPriceId}>
            {loading
              ? t('dragonBoat.processing')
              : proMonthlyPriceId
                ? t('dragonBoat.cta', { percent })
                : t('dragonBoat.loading')}
          </button>
          {error && <div className='dbf-banner-error'>{t(error)}</div>}
        </div>
      </div>
    </div>
  )
}

export default DragonBoatBanner
